import { Box, Button, Dialog, IconButton, Link, Paper, TextField, Typography, useTheme, CircularProgress } from "@mui/material";
import { genericFormProps } from "../signup.types";
import { useCallback, useContext, useEffect, useMemo, useState } from "react";
import LanguageIcon from '@mui/icons-material/Language';
import DeleteIcon from "../../../svg/DeleteIcon3";
import GlobeAddIcon from "../../../svg/GlobeAddIcon";
import GlobeIcon from "../../../svg/GlobeIcon";
import { useAuth } from "../../../hooks/useAuth";
import SitemapOptionsModal from "./webLinksModal";
import WeblinkIcon from "../../../svg/WeblinkIcon";
import SitemapIcon from "../../../svg/SitemapIcon";
import WarningIcon from "../../../svg/WarningIcon";
import ResyncIcon from "../../../svg/ResyncIcon";
import {TierContext} from "../../../context/TierContext";
import UpgradeTierCard from "../../upgradeTierCard";

const WebLinksForm: React.FC<genericFormProps> = (props) => {
  const {
    values,
    handleBlur,
    setFieldValue,
    showSteps
  } = props;

  const [webLink, setWebLink] = useState<string>('');
  const [sitemapTier, setSitemapTier] = useState<string>('train:sitemaps');
  const [weblinkErrors, setWeblinkErrors] = useState<string>(null);
  const [showSitemapModal, setShowSitemapModal] = useState<boolean>(false);
  const [showSitemapSwitchModal, setShowSitemapSwitchModal] = useState<boolean>(false);
  const [currentWebLink, setCurrentWebLink] = useState<string | null>(null);
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [deleteTarget, setDeleteTarget] = useState<any>(null);
  const {authUser} = useAuth()
  const theme = useTheme()

  const {checkPermission} = useContext(TierContext)

  const isLocked = useMemo(() => {
    return (authUser?.subscription?.pricing_plan?.tier ?? 0) < 3000;
  }, [authUser])

  const switchToSitemap = (link: string) => {
    if (!link) return
    setCurrentWebLink(link)
    setShowSitemapSwitchModal(true)
    return
  }

  const upgradeTier = () => {
    checkPermission(sitemapTier);
  }

  const processWeblink = (link?: string) => {
    if (!link && !webLink) return;
    let strippedLink = (link || webLink).trim();
    if (!strippedLink.startsWith("http")) {
      strippedLink = "https://" + strippedLink;
    }

    const validWebLink = /^(https?:\/\/)?(www\.)?[a-zA-Z0-9.-]+\.[a-z]{2,}([/?].*)?$/;
    if (!validWebLink.test(strippedLink)) {
      setWeblinkErrors("Please enter valid link to content");
      return;
    }

    setWeblinkErrors(null);
    setCurrentWebLink(strippedLink);  // always normalized
    setShowSitemapModal(true);
    setWebLink("");
  };

  const getStatusInfo = useCallback((item: any) => {
    if (item.is_indexed === false || !item.indexed_on) {
      return {
        text: 'In progress...',
        color: theme.palette.primary.main,
        showProgress: true,
        fullText: 'In progress...',
        date: null
      };
    }
    
    const syncedCount = item.llama_webpage_id?.length || 0;
    const totalCount = item.sitemap_links || 0;
    const indexedDate = item.indexed_on?.toDate?.();
    const dateStr = indexedDate ? indexedDate.toLocaleDateString('en-US', { 
      month: 'numeric', 
      day: 'numeric', 
      year: 'numeric' 
    }) : '';
    
    return {
      text: `${syncedCount}/${totalCount} Synced`,
      date: dateStr,
      fullText: dateStr ? `${syncedCount}/${totalCount} Synced: ${dateStr}` : `${syncedCount}/${totalCount} Synced`,
      color: 'text.primary',
      showProgress: false
    };
  }, [theme]);

  const handleResync = useCallback((item: any) => {
  if (item.type === 'weblink') {
    let copy = [...values.userWebPage || []];
    const index = copy.findIndex(x => x.web_link === item.web_link);
    if (index > -1) {
      copy[index] = {
        ...copy[index],
        is_indexed: false,
        indexed_on: null,
      } as any;
      setFieldValue("userWebPage", copy);
    }
  } else if (item.type === 'sitemap') {
    let copy = [...values.scrape_webpage || []];
    const index = copy.findIndex(x => x.web_link === item.web_link);
    if (index > -1) {
      copy[index] = {
        ...copy[index],
        is_indexed: false,
        indexed_on: null,
      } as any;
      setFieldValue("scrape_webpage", copy);
    }
  }
}, [values.userWebPage, values.scrape_webpage, setFieldValue]);

  const handleDownloadReport = (item: any) => {
    // TODO: Implement download report logic
    console.log("Download report for:", item.web_link);
    // You can add API call here to generate and download the report
  };

  const renderSitemapItem = useCallback((sitemap: any, index: number, onDeleteHandler: () => void) => {
    return (
      <Box key={`sitemap-${index}`} display='flex' justifyContent='space-between' alignItems='center' py={1.5} borderBottom={1} borderColor='divider' px={1.5}>
        <Box display='flex' alignItems='center'>
          <LanguageIcon sx={{ mr: 2, fontSize: '20px' }} />
          <Link href={sitemap.web_link} target="_blank" rel="noreferrer" underline="hover">
            <Typography variant='body2' sx={{ wordBreak: 'break-word' }}>{sitemap.web_link}</Typography>
          </Link>
        </Box>
        <IconButton color='error' size='small' onClick={onDeleteHandler}>
          <DeleteIcon stroke="error.light" />
        </IconButton>
      </Box>
    );
  }, []);

  const consolidatedLinks = [
    ...(values.userWebPage || [])
      .filter((item: any) => item.web_link?.trim() !== '') 
      .map((item: any) => ({ ...item, type: "weblink" })),
    ...(values.scrape_webpage || [])
      .filter((item: any) => item.web_link?.trim() !== '') 
      .map((item: any) => ({ ...item, type: "sitemap" })),
  ];


  const renderConsolidatedItem = useCallback(
    (item: any, index: number) => {
      let formatted =
        item.web_link.startsWith("http") || item.web_link.startsWith("https")
          ? item.web_link
          : "https://" + item.web_link;

      const status = getStatusInfo(item);

      return (
        <Box
          key={`item-${index}`}
          display="flex"
          flexDirection="column"
          borderBottom={1}
          borderColor="divider"
          px={2.5}
          py={1.5}
          gap={1}
        >
          {/* Top row: Icon + Link + Delete */}
          <Box
            display="flex"
            justifyContent="space-between"
            alignItems="center"
          >
            <Box sx={{ display: "flex", alignItems: "center", gap: 1.5 }}>
              {item.type === "weblink" ? (
                <WeblinkIcon stroke={theme.palette["nav"].iconactive} />
              ) : (
                <SitemapIcon stroke={theme.palette["nav"].iconactive} />
              )}
              <Link
                href={formatted}
                target="_blank"
                rel="noreferrer"
                underline="always"
              >
                <Typography
                  variant="body2"
                  sx={{ wordBreak: "break-word" }}
                  color="text.primary"
                >
                  {formatted}
                </Typography>
              </Link>
            </Box>

            <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
              {item.is_indexed && (
                <Box
                  sx={{
                    display: 'flex',
                    alignItems: 'center',
                    gap: 0.5,
                    px: 1,
                    py: 0.5,
                    cursor: 'pointer',
                    borderRadius: 1,
                    '&:hover': {
                      bgcolor: 'action.hover',
                    },
                  }}
                  onClick={() => handleResync(item)}
                >
                  <ResyncIcon 
                    width={24}
                    height={24}
                    style={{ opacity: 0.56 }}
                  />
                  <Typography
                    sx={{
                      color: 'text.primary',
                      fontSize: '12px',
                      fontWeight: 500,
                      lineHeight: '15.6px',
                      letterSpacing: '0.17px',
                    }}
                  >
                    Resync
                  </Typography>
                </Box>
              )}

              <IconButton
                id={`button-delete-${item.name}-${index}`}
                color='error'
                size='small'
                sx={{ width: '30px', height: '30px' }}
                disabled={!item.is_indexed}
                onClick={() => {
                  setDeleteTarget(item);
                  setShowDeleteModal(true);
                }}
              >
                <DeleteIcon
                  style={{
                    color: 'error.light',
                    fontSize: '18px',
                  }}
                  stroke={item.is_indexed ? theme.palette["nav"].iconactive : theme.palette.action.disabled}
                />
              </IconButton>
            </Box>
          </Box>

          <Box
            sx={{
              display: "flex",
              alignItems: "center",
              gap: 1.5,
              ml: 4,
              flexWrap: 'wrap',
            }}
          >
            <Box
              sx={{
                display: "flex",
                alignItems: "center",
                gap: 1,
              }}
            >
              <Box
                sx={{
                  width: 24,
                  height: 24,
                  position: 'relative',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                }}
              >
                {status.showProgress ? (
                  <CircularProgress size={20} sx={{ color: status.color }} />
                ) : (
                  <Box
                    sx={{
                      width: 20,
                      height: 20,
                      bgcolor: theme.palette.primary.main,
                      borderRadius: 1,
                      position: 'relative',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                    }}
                  >
                    <SitemapIcon 
                      fill={theme.palette.primary.main} 
                      stroke={theme.palette.primary.contrastText}
                      width="20px"
                      height="20px"
                    />
                  </Box>
                )}
              </Box>

              {status.showProgress ? (
                <Typography
                  sx={{
                    color: status.color,
                    fontSize: '12px',
                    fontWeight: 500,
                    lineHeight: '15.6px',
                    letterSpacing: '0.17px',
                  }}
                >
                  {status.fullText}
                </Typography>
              ) : (
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                  <Typography
                    component="span"
                    sx={{
                      color: 'text.primary',
                      fontSize: '12px',
                      fontWeight: 500,
                      lineHeight: '15.6px',
                      letterSpacing: '0.17px',
                    }}
                  >
                    {status.text}:
                  </Typography>
                  <Typography
                    component="span"
                    sx={{
                      color: 'text.secondary',
                      fontSize: '12px',
                      fontWeight: 500,
                      lineHeight: '15.6px',
                      letterSpacing: '0.17px',
                    }}
                  >
                    {status.date}
                  </Typography>
                </Box>
              )}
            </Box>

            {item.is_indexed && (
              <>
                <Box
                  sx={{
                    px: 1,
                    py: 0.5,
                    bgcolor: theme.palette.background["container-2"],
                    boxShadow: '0px 2px 12px rgba(0, 0, 0, 0.08)',
                    borderRadius: '18px',
                    border: `1px solid ${theme.palette.divider}`,
                    cursor: 'pointer',
                    '&:hover': {
                      bgcolor: theme.palette.action.hover,
                    },
                  }}
                  onClick={() => handleDownloadReport(item)}
                >
                  <Typography
                    sx={{
                      color: 'text.primary',
                      fontSize: '12px',
                      fontWeight: 500,
                      lineHeight: '15.6px',
                      letterSpacing: '0.17px',
                    }}
                  >
                    Download Report
                  </Typography>
                </Box>

                {item.sitemap_links > 0 && (
                  <Typography
                    sx={{
                      color: 'text.secondary',
                      fontSize: '12px',
                      fontWeight: 500,
                      textDecoration: 'underline',
                      lineHeight: '15.6px',
                      letterSpacing: '0.17px',
                      cursor: 'pointer',
                      '&:hover': {
                        color: 'text.primary',
                      },
                    }}
                    onClick={() => {
                      window.open(`${formatted}/sitemap.xml`, "_blank");
                    }}
                  >
                    View Sitemap
                  </Typography>
                )}
              </>
            )}

            {!item.is_indexed && item.sitemap_links > 0 && (
              <Typography
                sx={{
                  color: 'text.secondary',
                  fontSize: '12px',
                  fontWeight: 500,
                  textDecoration: 'underline',
                  lineHeight: '15.6px',
                  letterSpacing: '0.17px',
                  cursor: 'pointer',
                  '&:hover': {
                    color: 'text.primary',
                  },
                }}
                onClick={() => {
                  window.open(`${formatted}/sitemap.xml`, "_blank");
                }}
              >
                View Sitemap
              </Typography>
            )}
          </Box>

          {item.type === "weblink" && item.sitemap_links > 0 && !item.is_indexed && (
            <Box
              sx={{
                display: "flex",
                flexDirection: "row",
                alignItems: "center",
                gap: 1,
                mt: 1,
                ml: 4,
                position: "relative",
                "&::before": {
                  content: '""',
                  position: "absolute",
                  left: "-22px",
                  top: "-8px",
                  width: "22px",
                  height: "24px",
                  borderLeft: `2px solid ${theme.palette["_components"].input.standard.enabledBorder}`,
                  borderBottom: `2px solid ${theme.palette["_components"].input.standard.enabledBorder}`,
                  borderBottomLeftRadius: "8px",
                },
              }}
            >
              <Box
                sx={{
                  px: 1,
                  minWidth: "auto",
                  color: "text.secondary",
                  display: 'flex',
                  flexDirection: 'row',
                  gap: 1,
                  alignItems: 'center'
                }}
              >
                <Box sx={{
                  position: 'relative'
                }}>
                  <SitemapIcon 
                    fill={theme.palette.primary.main} 
                    stroke={theme.palette.primary.contrastText} 
                    radius={"10px"} 
                    style={{position: 'relative'}}
                  />
                  {isLocked && <WarningIcon style={{position:'absolute', right: -5, top: -2}}/>}
                </Box>
                <Button
                  size="small"
                  variant="outlined"
                  sx={{
                    bgcolor: theme.palette.secondary.main,
                    borderRadius: "16px !important",
                    border: `2px solid ${theme.palette.divider} !important`,
                    boxShadow: "0px 2px 12px 0px rgba(0, 0, 0, 0.08)",
                    px: 2,
                  }}
                  onClick={() => switchToSitemap(item.web_link)}
                >
                  <Typography variant="body3" color="text.primary">
                    Add {item.sitemap_links} Pages
                  </Typography>
                </Button>
              </Box>
              <Typography 
                variant="body3" 
                color="text.secondary" 
                sx={{textDecoration: "underline", cursor: 'pointer'}} 
                onClick={() => {
                  window.open(`${formatted}/sitemap.xml`, '_blank');
                }}
              >
                View Sitemap
              </Typography>
            </Box>
          )}
        </Box>
      );
    },
    [theme, getStatusInfo, isLocked, handleDownloadReport]
  );

  return (
    <Box
      sx={{
        display: "flex",
        flexDirection: "column",
        gap: 3,
      }}
    >
      {/* Page Title */}
      <Box>
        <Typography
          variant="h5"
          sx={{
            fontWeight: 600,
            color: 'text.primary',
            mb: 0.5,
          }}
        >
          Web Links
        </Typography>
        <Typography
          variant="body2"
          sx={{
            color: 'text.secondary',
          }}
        >
          Use websites to train your AI persona with your online content
        </Typography>
          <Typography
              sx={{
                  color: 'primary.main',
                  fontSize: 16,
                  fontFamily: '"Merriweather Sans", sans-serif',
                  fontWeight: 600,
                  lineHeight: '20.8px',
                  letterSpacing: '0.15px',
                  mb: 2,
                  mt: 2,
                }}
            >
               *One document or web link is required
            </Typography>
      </Box>

      {isLocked &&
        <UpgradeTierCard
          icon={<SitemapIcon fill={theme.palette.secondary.contrastText} stroke={theme.palette.primary.contrastText}/>} 
          title="Upgrade to Crawl All Your Sitemaps"
          description="Your can sync all pages of your website to train your AI persona with Ultimate tier."
          tier="Sitemap"
          buttonText="Upgrade to Ultimate"
        />
      }
      <Paper
        elevation={0}
        sx={{
          p:2,
          borderRadius: 4,
          border: `1px solid ${theme.palette.divider}`,
          bgcolor: theme.palette.background["container-2"]
        }}
      >
        <Box
          sx={{
              display:'flex',
              flexDirection:'column',
              gap:1,
          }}
        >
          <Box
            sx={{
              display:'flex',
              flexDirection: 'row',
              justifyContent: 'start',
              alignItems: 'center',
              gap:1,
            }}
          >
            <GlobeAddIcon fill={theme.palette.secondary.contrastText} color={theme.palette.primary.contrastText}/>
            <Typography variant='h7'>Add Weblink</Typography>
          </Box>
          <Typography variant='body2'>Use websites to train your AI persona.</Typography>
        </Box>
        <Box
          sx={{
            display: 'flex',
            flexDirection: {xs:'column',md:'row'},
            alignItems: 'center',
            gap: 1,
            mt:2,
          }}
        >
          <TextField
            type="url"
            fullWidth
            name='google_drive_links'
            margin='none'
            placeholder="http://"
            value={webLink}
            onChange={(e) => {
              setWebLink(e.target.value)
            }}
            onBlur={handleBlur}
            error={Boolean(weblinkErrors)}
            helperText={weblinkErrors}
            slotProps={{
              input: {
                sx: {
                  backgroundColor: (theme) => theme.palette.mode === 'dark' ?
                    theme.palette.background.paper : 'common.white',
                  color: 'text.primary',
                  height: '100%',
                  borderRadius: 2,
                  "& fieldset": {
                    border: "2px solid #42424250",
                  }
                }
              }
            }}
            variant='outlined'
            sx={{
              height: '40px',
              flex: 1,
              '& .MuiInputBase-root': {
                height: '100%'
              }
            }}
          />
          <Button
            variant="contained"
            disabled={!webLink}
            sx={{
              height: {xs: 40, md:'-webkit-fill-available',},
              width: {xs:'100%',md:'100px'},
              bgcolor: theme.palette.primary.main
            }}
            onClick={() => processWeblink()}
          >+ Add</Button>
        </Box>
      </Paper>
      {consolidatedLinks.length > 0 && (
        <Box mt={3}>
          <Paper
            elevation={0}
            sx={{
              p: 2,
              borderRadius: 3,
              border: `1px solid ${theme.palette.divider}`,
              display: "flex",
              flexDirection: "row",
              alignItems: {xs: 'flex-start', md:"center"},
              justifyContent: 'space-between',
              bgcolor: theme.palette.background["container-2"]
            }}
          >
            <Box
              sx={{
                display: "flex",
                flexDirection: "row",
                alignItems: "center",
                gap: 1,
              }}
            >
              <GlobeIcon fill={theme.palette.secondary.contrastText} color={theme.palette.primary.contrastText} />
              <Typography variant="h7">My Weblinks</Typography>
            </Box>
            <Box
              sx={{
                display: "flex",
                flexDirection: { xs: "column", md: "row" },
                alignItems: { xs: "flex-end", md: "center" },
                gap: { xs: 1, md: 2 },
              }}
            >
              <Box
                sx={{
                  display: "flex",
                  flexDirection: "row",
                  alignItems: "center",
                  gap: 1,
                }}
              >
                <WeblinkIcon stroke={theme.palette["nav"].iconactive} />
                <Typography fontSize={"0.75rem"} color="text.secondary">= Weblinks</Typography>
              </Box>
              <Box
                sx={{
                  display: "flex",
                  flexDirection: "row",
                  alignItems: "center",
                  gap: 0.25,
                }}
              >
                <SitemapIcon stroke={theme.palette["nav"].iconactive} />
                <Typography fontSize={"0.75rem"} color="text.secondary">= Sitemap</Typography>
              </Box>
            </Box>
          </Paper>

          {consolidatedLinks.map((item, i) =>
            renderConsolidatedItem(item, i))}
        </Box>
      )}

      <SitemapOptionsModal
        open={showSitemapModal}
        onClose={() => {
          setShowSitemapModal(false)
          setCurrentWebLink('')
        }}
        webLink={currentWebLink ?? ""}
        values={values}
        setFieldValue={setFieldValue}
        isLocked={isLocked}
        upgradeTier={upgradeTier}
      />

      <SitemapSwitchToSitemapModal
        webLink={currentWebLink ?? ""}
        open={showSitemapSwitchModal}
        onClose={() => { setShowSitemapSwitchModal(false) }}
        setFieldValue={setFieldValue}
        isLocked={isLocked}
        values={values}
        upgradeTier={upgradeTier}
      />

      <DeleteConfirmationModal
        open={showDeleteModal}
        onClose={() => setShowDeleteModal(false)}
        webLink={deleteTarget?.web_link ?? ""}
        sitemapCount={deleteTarget?.sitemap_count ?? deleteTarget?.sitemap_links ?? 0}
        onConfirm={() => {
          if (deleteTarget?.type === 'weblink') {
            let copy = [...values.userWebPage || []];
            const index = copy.findIndex(x => x.web_link === deleteTarget?.web_link);
            if (index > -1) copy.splice(index, 1);
            setFieldValue("userWebPage", copy);
          }
          else {
            let copy = [...values.scrape_webpage || []];
            const index = copy.findIndex(x => x.web_link === deleteTarget?.web_link);
            if (index > -1) copy.splice(index, 1);
            setFieldValue("scrape_webpage", copy);
          }
          setShowDeleteModal(false);
        }}
      />
    </Box>
  )
}

export default WebLinksForm;


interface SitemapSwitchToSitemapModalProps {
  webLink?: string;                  // the link passed from WebLinksForm
  values: any;                       // form values (Formik)
  setFieldValue: (field: string, value: any) => void;
  open: boolean;
  onClose: () => void;
  isLocked?: boolean;
  upgradeTier: () => void;
}
const SitemapSwitchToSitemapModal: React.FC<SitemapSwitchToSitemapModalProps> = (props) => {

  const { webLink, open, onClose, setFieldValue, isLocked, values, upgradeTier } = props;
  const theme = useTheme()

  const switchToSitemap = () => {
    // Remove from userWebPage
    const webPageIndex = values.userWebPage.findIndex(
      (page: any) => page.web_link === webLink
    );

    let sitemapLinks = 0;

    if (webPageIndex !== -1) {
      // Get sitemap links
      sitemapLinks = values.userWebPage[webPageIndex]?.sitemap_links ?? 0;

      // Remove from userWebPage
      let webPageCopy = [...values.userWebPage];
      webPageCopy.splice(webPageIndex, 1);
      setFieldValue('userWebPage', webPageCopy);
    }

    // Add to scrape_webpage
    let scrapeWebpageCopy = [...values.scrape_webpage];
    scrapeWebpageCopy.push({
      web_link: webLink,
      updatedAt: new Date().toISOString(),
      sitemap_links: sitemapLinks
    });
    setFieldValue('scrape_webpage', scrapeWebpageCopy);

    onClose();
  }

  return (
    <Dialog
      open={open}
      onClose={onClose}
      sx={{
        "& .MuiDialog-paper": {
          borderRadius: 4,
          overflow: "hidden",
        },
      }}
      slotProps={{
        backdrop: {
          style: {
            backdropFilter: 'blur(4px)',
          }
        }
      }}
    >
      <Box sx={{ p: 3, maxWidth: "450px" }}>
        {isLocked ?
          <Box
            sx={{
              display: 'flex',
              flexDirection: 'column',
              gap: 2,
            }}
          >
            <Box
              sx={{
                display: 'flex',
                flexDirection: 'row',
                alignItems: 'center',
                gap: 1
              }}
            >
              <SitemapIcon fill={theme.palette.primary.main} stroke={theme.palette.primary.contrastText} width={'25px'} height={'25px'} />
              <Typography variant="h6">This Weblink Contains a Sitemap </Typography>
            </Box>
            <Box>
              <Typography variant="body1"><strong>{webLink}</strong> contains a sitemap. Upgrade to <strong>Ultimate tier</strong> to sync this sitemap and all connected webpages to your AI persona's training material.</Typography>
            </Box>
            <Box sx={{
              display: 'flex',
              flexDirection: { xs: 'column-reverse', md: 'row' },
              justifyContent: { xs: 'start', md: 'flex-end' },
              gap: { xs: 1, md: 1 },
            }}>
              <Button
                variant="contained"
                sx={{
                  bgcolor: `${theme.palette.secondary.main} !important`,
                  border: `1px solid ${theme.palette.secondary._states.outlinedBorder} !important`
                }}
                onClick={onClose}
              >
                <Typography sx={{ color: theme.palette.secondary.contrastText }}>
                  Cancel
                </Typography>
              </Button>
              <Button
                variant="contained"
                sx={{
                  bgcolor: `${theme.palette.primary.main}`,
                }}
                onClick={upgradeTier}
              >
                <Typography sx={{ color: theme.palette.primary.contrastText }}>
                  Upgrade Subscription
                </Typography>
              </Button>
            </Box>
          </Box>
          :
          <Box
            sx={{
              display: 'flex',
              flexDirection: 'column',
              gap: 2
            }}
          >
            <Box
              sx={{
                display: 'flex',
                flexDirection: 'column',
                alignItems: 'start',
                gap: 1
              }}
            >
              <Box
                sx={{
                  display: 'flex',
                  flexDirection: 'row',
                  alignItems: 'center',
                  gap: 1
                }}
              >
                <SitemapIcon fill={theme.palette.primary.main} stroke={theme.palette.primary.contrastText} width={'25px'} height={'25px'} />
                <Typography variant="h6">This Weblink Contains a Sitemap </Typography>
              </Box>
              <Typography variant="body2" color="text.secondary">You can sync this sitemap and all connected webpages to your AI persona’s training material. </Typography>
            </Box>
            <Button
              variant="contained"
              sx={{
                bgcolor: `${theme.palette.primary.main}`,
                width: '-webkit-fill-available !important',
              }}
              onClick={switchToSitemap}
            >
              Sync All Pages
            </Button>
          </Box>
        }
      </Box>
    </Dialog>
  );
}

interface DeleteConfirmationModalProps {
  open: boolean;
  onClose: () => void;
  onConfirm: () => void;
  webLink: string;
  sitemapCount?: number;
}

const DeleteConfirmationModal: React.FC<DeleteConfirmationModalProps> = ({
  open,
  onClose,
  onConfirm,
  webLink,
  sitemapCount = 0,
}) => {
  const theme = useTheme();

  return (
    <Dialog
      open={open}
      onClose={onClose}
      sx={{
        "& .MuiDialog-paper": {
          borderRadius: 4,
          padding: 3,
          maxWidth: "400px",
        },
      }}
      slotProps={{
        backdrop: {
          style: {
            backdropFilter: 'blur(4px)',
          }
        }
      }}
    >
      <Box display="flex" flexDirection="column" gap={1}>
        {/* Title Row */}
        <Box display="flex" alignItems="center" gap={1}>
          <DeleteIcon stroke={theme.palette.error.main} />
          <Typography variant="h6" color="text.primary">
            Delete Webpage and Sitemap?
          </Typography>
        </Box>

        {/* Content */}
        <Typography variant="body2" color="text.primary">
          <strong>{webLink}</strong>
        </Typography>
        <Typography variant="body2" color="text.secondary">
          Are you sure you want to delete this webpage and sitemap?
          {sitemapCount > 0 && (
            <> You will delete <strong>{sitemapCount}</strong> linked pages.</>
          )}
        </Typography>

        {/* Buttons */}
        <Box display="flex" justifyContent="flex-end" gap={1} mt={2}>
          <Button
            variant="contained"
            sx={{
              bgcolor: `${theme.palette.secondary.main} !important`,
              border: `1px solid ${theme.palette.secondary._states.outlinedBorder} !important`
            }}
            onClick={onClose}
          >
            <Typography sx={{ color: theme.palette.secondary.contrastText }}>
              Cancel
            </Typography>
          </Button>
          <Button
            variant="contained"
            color="error"
            startIcon={<DeleteIcon width={20} height={20} stroke={theme.palette.error.contrastText} />}
            onClick={onConfirm}
            sx={{ borderRadius: 2, px: 3 }}
          >
            Delete
          </Button>
        </Box>
      </Box>
    </Dialog>
  );
};